import React, { memo } from "react";
import { ArrowDown, ArrowUp, Bell, Calendar, ChevronsUp, Equal } from "lucide-react";
import Avatar from "../ui/Avatar";
import { ProjectLabel, TaskTypeBadge, formatDate } from "./shared";
import { dragState } from "./dragState";
import SelectCheckbox from "./SelectCheckbox";
import { PRIORITIES } from "./constants";

const PRIORITY_STYLE = {
  critical: ["text-danger", ChevronsUp],
  high: ["text-danger", ArrowUp],
  medium: ["text-warn", Equal],
  low: ["text-faint", ArrowDown],
};

const Priority = ({ priority }) => {
  const p = PRIORITIES.find((x) => x.value === priority);
  if (!p) return null;
  const [cls, Icon] = PRIORITY_STYLE[priority];
  return (
    <span className={`ml-auto inline-flex items-center gap-1 text-[11.5px] font-medium ${cls}`}>
      <Icon className="w-3.5 h-3.5" /> {p.label}
    </span>
  );
};

/**
 * One task card. Labels (project, squad, sprint) are resolved from the lookup
 * maps built from /options — never from text stored on the task.
 */
const TaskCard = ({ task, lookups, draggable, pending, hasUnread, selectable, selected, onToggleSelect, onOpen, onDragStart, onDragEnd }) => {
  const projects = (task.projects || []).map((id) => lookups.projects[id]).filter(Boolean);
  const assignees = task.assignees || [];
  const squad = lookups.squads[task.squad];
  const due = task.dueDate ? new Date(task.dueDate) : null;
  const dueToday = due && task.status !== "done" && due.toDateString() === new Date().toDateString();
  // From the server: stays true after the task is done if it finished late.
  const overdue = task.deadlineMissed ?? (due && task.status !== "done" && !dueToday && due < new Date());
  const lateLabel = overdue
    ? task.status === "done"
      ? `Finished ${task.daysLate || 1} day${(task.daysLate || 1) === 1 ? "" : "s"} late`
      : `Overdue${task.daysLate ? ` by ${task.daysLate} day${task.daysLate === 1 ? "" : "s"}` : ""}`
    : null;
  const qaFails = task.qaRejectedCount || 0;

  return (
    <article
      draggable={draggable}
      data-task-id={task._id}
      onDragStart={(e) => {
        e.dataTransfer.setData("taskId", task._id);
        e.dataTransfer.effectAllowed = "move";
        dragState.taskId = task._id;
        onDragStart?.(task);
      }}
      onDragEnd={(e) => {
        dragState.taskId = null;
        onDragEnd?.(e);
      }}
      onClick={() => onOpen(task)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(task)}
      tabIndex={0}
      role="button"
      aria-label={`Task #${task.number}: ${task.title}`}
      className={`group bg-panel rounded-xl p-3 border transition-all duration-200 outline-none hover:-translate-y-0.5 hover:border-accent-line hover:shadow-[0_12px_28px_-14px_rgb(var(--c-accent)/0.55)] ${
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } ${overdue ? "border-danger/50" : selected ? "border-accent-line ring-2 ring-accent-soft" : "border-line"} ${pending ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-1.5 mb-2">
        {selectable && (
          <SelectCheckbox
            checked={Boolean(selected)}
            onChange={() => onToggleSelect(task._id)}
            label={`Select task #${task.number} for a sprint`}
            title={selected ? "Deselect" : "Select for a sprint"}
            className="-ml-0.5 p-1"
          />
        )}
        <span className="font-mono text-[11px] text-faint">#{task.number}</span>
        <TaskTypeBadge type={task.taskType} />
        {qaFails > 0 && (
          <span
            className="font-mono text-[10.5px] px-1.5 py-px rounded-md bg-danger-soft text-danger"
            title={`Rejected by QA ${qaFails} time${qaFails === 1 ? "" : "s"}`}
          >
            QA ✕{qaFails}
          </span>
        )}
        <Priority priority={task.priority} />
        {hasUnread && (
          <span
            className={`${task.priority ? "" : "ml-auto"} inline-flex items-center justify-center w-5 h-5 rounded-full bg-danger text-white shadow-sm`}
            title="New comments"
            aria-label="New comments"
          >
            <Bell className="w-3 h-3" />
          </span>
        )}
      </div>

      <h3 className="text-[14px] font-medium text-ink leading-snug line-clamp-3 break-words">
        {task.title}
      </h3>

      {projects.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {projects.map((p) => (
            <ProjectLabel key={p._id} project={p} size="xs" />
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 text-[11.5px] text-faint">
        <div className="flex items-center gap-2 min-w-0">
          {squad && (
            <span className="inline-flex items-center gap-1 min-w-0" title={`Squad: ${squad.name}`}>
              <span className="truncate">{squad.name}</span>
            </span>
          )}
          {task.dueDate && (
            <span
              className={`inline-flex items-center gap-1 shrink-0 ${overdue ? "text-danger font-medium" : dueToday ? "text-accent-ink font-medium" : ""}`}
              title={lateLabel || (dueToday ? "Due today" : "Due date")}
            >
              <Calendar className="w-3 h-3" />
              {dueToday ? "Today" : formatDate(task.dueDate, { day: "numeric", month: "short" })}
            </span>
          )}
        </div>
        {assignees.length ? (
          <span
            className="flex -space-x-1.5 shrink-0"
            title={assignees.map((a) => a.fullNameEn || a.nickname).join(", ")}
          >
            {assignees.slice(0, 3).map((a) => (
              <Avatar key={a._id} src={a.photo} name={a.fullNameEn} size="xs" className="ring-2 ring-[var(--panel-solid)]" />
            ))}
            {assignees.length > 3 && (
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium bg-gray-200 dark:bg-dark-200 text-gray-700 dark:text-light-300 ring-2 ring-[var(--panel-solid)]">
                +{assignees.length - 3}
              </span>
            )}
          </span>
        ) : (
          <span className="italic shrink-0 text-faint">Unassigned</span>
        )}
      </div>
    </article>
  );
};

export default memo(TaskCard);
